import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Alert,
  Checkbox,
  DatePicker,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  StopOutlined,
  TeamOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  getSimulates,
  startSimulate,
  cancelSimulate,
} from '../../api/drill';
import { getDevices } from '../../api/device';
import dayjs from 'dayjs';
import apiError from '../../utils/apiError';
import './index.css';

const Simulates = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [startModalVisible, setStartModalVisible] = useState(false);
  const [devicesModalVisible, setDevicesModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceSearchText, setDeviceSearchText] = useState('');
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasStartedSimulate, setHasStartedSimulate] = useState(false);
  const [modalErrorMsg, setModalErrorMsg] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [startForm] = Form.useForm();

  // 获取当前用户信息和角色
  const [currentUser, setCurrentUser] = useState(null);
  const [userFilter, setUserFilter] = useState(''); // 用户名过滤条件

  useEffect(() => {
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      setCurrentUser(JSON.parse(userInfo));
    }
  }, []);

  // 判断是否为管理员
  const isAdmin = currentUser?.role === 'admin';

  const simulateTypeMap = {
    1: '设备本地',
    2: '管理平台web',
    3: '管理平台APP/小程序/服务号',
    4: '联机设备',
  };

  // 获取演习记录列表
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pageSize,
      };

      // 如果是管理员且设置了用户过滤条件，添加 user 参数
      if (isAdmin && userFilter && userFilter.trim()) {
        params.user = userFilter.trim();
      }

      const res = await getSimulates(params);
      setRecords(res.data.records || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      // 全局 Alert 已自动显示错误
    } finally {
      setLoading(false);
    }
  };

  // 获取设备列表（支持搜索）
  const fetchDevices = async (searchText = '') => {
    setDeviceLoading(true);
    try {
      const params = {
        page: 1,
        pageSize: 100,
      };

      // 如果有搜索文本，添加 deviceId 参数
      if (searchText && searchText.trim()) {
        params.deviceId = searchText.trim();
      }

      const res = await getDevices(params);
      const deviceList = res.data.devices || [];
      setDevices(deviceList);
      return deviceList;
    } catch (error) {
      // 全局 Alert 已自动显示错误
      return [];
    } finally {
      setDeviceLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // 初始加载设备列表
    fetchDevices();
  }, [page, pageSize, userFilter, isAdmin]); // 添加 userFilter 和 isAdmin 依赖

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 定时更新当前时间，用于倒计时计算
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 判断演习是否还在进行中（倒计时未结束）
  const isSimulateActive = (record) => {
    if (!record) return false;

    const countdown = (record.countdown || 10) * 1000; // 转换为毫秒
    let endTime;

    // 如果指定了未来时间
    if (record.startAt) {
      // 倒计时结束时间 = 未来时间 + 倒计时时间
      endTime = record.startAt + countdown;
    } else {
      // 没有指定时间，使用演习时间或创建时间
      const startTime = record.simulatedAt || record.createdAt;
      if (!startTime) return false;
      // 倒计时结束时间 = 演习开始时间 + 倒计时时间
      endTime = startTime + countdown;
    }

    return currentTime < endTime;
  };

  // 计算剩余倒计时（秒）
  const getRemainingTime = (record) => {
    if (!record) return 0;

    const countdown = (record.countdown || 10) * 1000; // 转换为毫秒
    let endTime;

    // 如果指定了未来时间
    if (record.startAt) {
      endTime = record.startAt + countdown;
    } else {
      const startTime = record.simulatedAt || record.createdAt;
      if (!startTime) return 0;
      endTime = startTime + countdown;
    }

    const remaining = Math.max(0, Math.floor((endTime - currentTime) / 1000));
    return remaining;
  };

  // 发起演习
  const handleStartSimulate = () => {
    setModalErrorMsg('');
    setStartModalVisible(true);
    setHasStartedSimulate(false);
    startForm.resetFields();
    setDeviceSearchText('');
    // 重新加载设备列表
    fetchDevices();
  };

  // 处理设备搜索（回车触发）
  const handleDeviceSearch = (value) => {
    fetchDevices(value);
  };

  const handleStartSubmit = async () => {
    try {
      const values = await startForm.validateFields();

      // 自动去重设备 ID
      const uniqueIds = [...new Set(values.ids)];

      const data = {
        ids: uniqueIds,
        magnitude: values.magnitude,
        countdown: values.countdown || 10,
      };

      // 如果设置了演习开始时间,添加到请求中
      if (values.startDateTime) {
        data.startAt = new Date(values.startDateTime).getTime();
      }
      data.simulateType=2
      const res = await startSimulate(data);
      message.success(`演习启动成功，事件ID: ${res.data.eventId}`);
      setHasStartedSimulate(true);
      setStartModalVisible(false);
      startForm.resetFields();
      setDeviceSearchText('');
      fetchRecords();
    } catch (error) {
      console.error('启动演习失败', error);
      setModalErrorMsg(error.message || '启动演习失败');
      apiError.clear();
    }
  };

  // 查看参与设备
  const handleViewDevices = (record) => {
    setSelectedRecord(record);
    setDevicesModalVisible(true);
  };

  // 取消演习
  const handleCancelSimulate = (record) => {
    setSelectedRecord(record);
    setSelectedDeviceIds([]);
    setModalErrorMsg('');
    setCancelModalVisible(true);
  };

  const handleCancelSubmit = async () => {
    if (selectedDeviceIds.length === 0) {
      message.warning('请至少选择一个设备');
      return;
    }

    try {
      await cancelSimulate({
        ids: selectedDeviceIds,
        eventId: selectedRecord.eventId,
      });
      message.success('取消演习指令已发送');
      setCancelModalVisible(false);
      setSelectedDeviceIds([]);
    } catch (error) {
      console.error('取消演习失败', error);
      setModalErrorMsg(error.message || '取消演习失败');
      apiError.clear();
    }
  };

  const columns = [
    {
      title: '事件ID',
      dataIndex: 'eventId',
      key: 'eventId',
      width: isMobile ? 80 : 120,
    },
    {
      title: '演习时间',
      dataIndex: 'simulatedAt',
      key: 'simulatedAt',
      width: isMobile ? 100 : 180,
      render: (timestamp) =>
        timestamp
          ? (isMobile
              ? dayjs(timestamp).format('MM-DD HH:mm')
              : dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'))
          : '-',
    },
    {
      title: '震级',
      dataIndex: 'magnitude',
      key: 'magnitude',
      width: isMobile ? 60 : 100,
      render: (magnitude) => magnitude ? `${magnitude.toFixed(1)}级` : '-',
    },
    ...(!isMobile ? [
      {
        title: '经度',
        dataIndex: 'longitude',
        key: 'longitude',
        width: 120,
        render: (value) => value ? value.toFixed(4) : '-',
      },
      {
        title: '纬度',
        dataIndex: 'latitude',
        key: 'latitude',
        width: 120,
        render: (value) => value ? value.toFixed(4) : '-',
      },
    ] : []),
    {
      title: '震中',
      dataIndex: 'epicenter',
      key: 'epicenter',
      width: isMobile ? 80 : 200,
      ellipsis: true,
    },
    {
      title: isMobile ? '设备' : '演习设备数量',
      dataIndex: 'deviceNum',
      key: 'deviceNum',
      width: isMobile ? 60 : 120,
      render: (num, record) => (
        <Button
          type="link"
          size="small"
          icon={<TeamOutlined />}
          onClick={() => handleViewDevices(record)}
          style={{ padding: isMobile ? '0 4px' : undefined }}
        >
          {num}{isMobile ? '' : ' 台'}
        </Button>
      ),
    },
    ...(!isMobile ? [
      {
        title: '演习方式',
        dataIndex: 'simulateType',
        key: 'simulateType',
        width: 180,
        render: (type) => (
          <Tag color="blue">{simulateTypeMap[type] || type}</Tag>
        ),
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (timestamp) =>
          timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
    ] : []),
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 100 : 150,
      fixed: 'right',
      render: (_, record) => {
        const isActive = isSimulateActive(record);
        const remainingTime = getRemainingTime(record);

        return isActive ? (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancelSimulate(record)}
              style={{ padding: isMobile ? '0 4px' : undefined }}
            >
              {isMobile ? '取消' : '取消演习'}
            </Button>
            {!isMobile && (
              <span style={{ fontSize: 12, color: '#999' }}>
                剩余 {remainingTime}秒
              </span>
            )}
          </Space>
        ) : (
          <span style={{ color: '#999' }}>已结束</span>
        );
      },
    },
  ];

  return (
    <div className="simulates-container">
      <h2 className="page-title">地震演习管理</h2>

      <Card>
        <div className="table-toolbar">
          <Space>
            {isAdmin && (
              <>
                <Input
                  placeholder="搜索用户名/手机号（回车搜索）"
                  prefix={<SearchOutlined />}
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  onPressEnter={() => {
                    setPage(1); // 搜索时重置到第一页
                    fetchRecords();
                  }}
                  style={{ width: 250 }}
                  allowClear
                  onClear={() => {
                    setUserFilter('');
                    setPage(1);
                  }}
                />
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setPage(1); // 搜索时重置到第一页
                    fetchRecords();
                  }}
                >
                  搜索
                </Button>
              </>
            )}
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleStartSimulate}
            >
              发起演习
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchRecords}>
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          scroll={{ x: isMobile ? 500 : 1400 }}
          size={isMobile ? 'small' : 'middle'}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: !isMobile,
            showTotal: isMobile ? undefined : (total) => `共 ${total} 条`,
            simple: isMobile,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* 发起演习模态框 */}
      <Modal
        title="发起地震演习"
        open={startModalVisible}
        onOk={handleStartSubmit}
        onCancel={() => {
          setStartModalVisible(false);
          setModalErrorMsg('');
          startForm.resetFields();
          // 如果已经成功发起演习，关闭时刷新列表
          if (hasStartedSimulate) {
            fetchRecords();
          }
        }}
        width={600}
      >
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {modalErrorMsg && (
            <Alert message={modalErrorMsg} type="error" showIcon closable onClose={() => setModalErrorMsg('')} />
          )}
        </div>
        <Form form={startForm} layout="vertical">
          <Form.Item
            name="ids"
            label="选择设备"
            rules={[{ required: true, message: '请选择至少一个设备' }]}
            extra="输入设备ID后按回车键搜索，支持模糊匹配"
          >
            <Select
              mode="multiple"
              placeholder="请输入设备ID搜索，支持多选"
              loading={deviceLoading}
              showSearch
              searchValue={deviceSearchText}
              onSearch={setDeviceSearchText}
              filterOption={false}
              onInputKeyDown={(e) => {
                // 回车触发搜索
                if (e.key === 'Enter' && deviceSearchText) {
                  e.preventDefault();
                  handleDeviceSearch(deviceSearchText);
                }
              }}
              notFoundContent={
                deviceLoading ? '搜索中...' : devices.length === 0 ? '无匹配设备，请尝试其他关键词' : null
              }
              options={devices.map((device) => ({
                label: `${device.deviceId}${
                  device.lng != null && device.lat != null
                  && !isNaN(Number(device.lng)) && !isNaN(Number(device.lat))
                  ? ` (${device.lng}, ${device.lat})`
                  : ' (无位置信息)'
                }`,
                value: device.deviceId,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="magnitude"
            label="震级"
            rules={[{ required: true, message: '请输入震级' }]}
          >
            <InputNumber
              min={0}
              max={10}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="例如: 6.5"
            />
          </Form.Item>

          <Form.Item
            name="countdown"
            label="倒计时(秒)"
            initialValue={10}
            rules={[{ required: true, message: '请输入倒计时' }]}
            extra="震中位置将根据设备位置和倒计时自动计算"
          >
            <InputNumber
              min={0}
              max={300}
              style={{ width: '100%' }}
              placeholder="例如: 10"
            />
          </Form.Item>

          <Form.Item
            name="startDateTime"
            label="演习开始时间"
            extra="可选，不填写则立即开始"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              placeholder="选择演习开始时间"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看参与设备模态框 */}
      <Modal
        title={`演习参与设备 (共 ${selectedRecord?.deviceNum || 0} 台)`}
        open={devicesModalVisible}
        onCancel={() => {
          setDevicesModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={800}
      >
        {selectedRecord && selectedRecord.deviceIds && (
          <Table
            dataSource={selectedRecord.deviceIds.map((id, index) => ({
              key: index,
              deviceId: id,
            }))}
            rowKey="key"
            pagination={false}
            size="small"
            columns={[
              {
                title: '序号',
                key: 'index',
                width: 80,
                render: (_, __, index) => index + 1,
              },
              {
                title: '设备ID',
                dataIndex: 'deviceId',
                key: 'deviceId',
              },
            ]}
          />
        )}
      </Modal>

      {/* 取消演习模态框 */}
      <Modal
        title="取消演习"
        open={cancelModalVisible}
        onOk={handleCancelSubmit}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedDeviceIds([]);
          setSelectedRecord(null);
          setModalErrorMsg('');
        }}
        width={600}
      >
        <div style={{ minHeight: 42, marginBottom: 16 }}>
          {modalErrorMsg && (
            <Alert message={modalErrorMsg} type="error" showIcon closable onClose={() => setModalErrorMsg('')} />
          )}
        </div>
        <p style={{ marginBottom: 16 }}>
          请选择要取消演习的设备（可多选）：
        </p>
        {selectedRecord && selectedRecord.deviceIds && (
          <Checkbox.Group
            style={{ width: '100%' }}
            value={selectedDeviceIds}
            onChange={setSelectedDeviceIds}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {selectedRecord.deviceIds.map((id) => (
                <Checkbox key={id} value={id}>
                  {id}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        )}
      </Modal>
    </div>
  );
};

export default Simulates;
